/**
 * zcom logo — geometry of the dot spiral and the four letters, as plain numbers.
 *
 * The mark is a sunflower spiral: every dot sits a golden angle on from the
 * last one and a little further out, coloured round the wheel starting at the
 * brand blue. The wordmark is drawn, not typeset — four strokes of one weight —
 * so it looks the same whatever font the page loads, the browser translator
 * finds no text to rewrite, and each letter can be animated as a line being
 * written.
 *
 * Two lockups:
 *   badge  — the word set into the spiral from the left (sign-in, splash).
 *   inline — the spiral, then the word (page headers, footers).
 *
 * Pure: no React, no DOM. components/ZcomLogo.jsx draws it and
 * scripts/build-zcom-logo.mjs writes the standalone files in public/brand.
 */

const GOLDEN_ANGLE = Math.PI * (3 - Math.sqrt(5));
const TAU = 2 * Math.PI;

/** Colours round the wheel. ZcomLogo.css repeats them in @keyframes zl-hue. */
export const PALETTE = [
  '#1a4f8b', '#2a75d3', '#1fb6e0', '#14b8a6', '#6cc644', '#f4d01e',
  '#fca311', '#f3722c', '#e63946', '#d6336c', '#8e3bc4', '#4338ca',
];

/** Brand ink for the wordmark on a light page. */
export const INK = '#1a4f8b';

const rgb = (hex) => [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16));

/** Colour at `turn` (0–1, wraps) round the wheel, blended between neighbours. */
export const colourAt = (turn) => {
  const f = (((turn % 1) + 1) % 1) * PALETTE.length;
  const i = Math.floor(f) % PALETTE.length;
  const a = rgb(PALETTE[i]);
  const b = rgb(PALETTE[(i + 1) % PALETTE.length]);
  return `#${a.map((v, k) => Math.round(v + (b[k] - v) * (f - i)).toString(16).padStart(2, '0')).join('')}`;
};

const round = (n) => Math.round(n * 100) / 100;

// ── Letters ────────────────────────────────────────────────────────────────
// A letter is a list of strokes, a stroke a list of pieces: a straight line
// ['L', x1, y1, x2, y2] or an arc ['A', cx, cy, r, from, to] (radians, growing
// clockwise on screen). One description gives both the path and the distance
// from any point to the letter, which is what clears the dots around the word.

const STROKE = 13;      // weight
const BODY = 39;        // x-height, centre line to centre line
const BOWL = 19.5;      // radius of c and o
const ARCH = 12.5;      // radius of the arches of m
const Z_WIDTH = 30;
const GAP = 8;

const pointOnArc = ([, cx, cy, r], a) => [cx + r * Math.cos(a), cy + r * Math.sin(a)];
const startOf = (p) => (p[0] === 'L' ? [p[1], p[2]] : pointOnArc(p, p[4]));
const endOf = (p) => (p[0] === 'L' ? [p[3], p[4]] : pointOnArc(p, p[5]));

const strokePath = (pieces) => {
  const [sx, sy] = startOf(pieces[0]);
  return `M${round(sx)} ${round(sy)}${pieces.map((p) => {
    const [ex, ey] = endOf(p);
    if (p[0] === 'L') return `L${round(ex)} ${round(ey)}`;
    const sweep = p[5] - p[4];
    return `A${round(p[3])} ${round(p[3])} 0 ${Math.abs(sweep) > Math.PI ? 1 : 0} ${sweep > 0 ? 1 : 0} ${round(ex)} ${round(ey)}`;
  }).join('')}`;
};

const distanceToPiece = (p, x, y) => {
  if (p[0] === 'L') {
    const [, x1, y1, x2, y2] = p;
    const dx = x2 - x1;
    const dy = y2 - y1;
    const t = Math.max(0, Math.min(1, ((x - x1) * dx + (y - y1) * dy) / (dx * dx + dy * dy)));
    return Math.hypot(x - x1 - t * dx, y - y1 - t * dy);
  }
  const [, cx, cy, r, from, to] = p;
  const lo = Math.min(from, to);
  const span = Math.abs(to - from);
  const a = Math.atan2(y - cy, x - cx);
  const along = ((((a - lo) % TAU) + TAU) % TAU);
  if (along <= span) return Math.abs(Math.hypot(x - cx, y - cy) - r);
  return Math.min(...[from, to].map((e) => {
    const [ex, ey] = pointOnArc(p, e);
    return Math.hypot(x - ex, y - ey);
  }));
};

const LETTERS = {
  z: (x, cy, s) => {
    const l = x + (STROKE / 2) * s;
    const r = l + Z_WIDTH * s;
    const t = cy - (BODY / 2) * s;
    const b = cy + (BODY / 2) * s;
    return { strokes: [[['L', l, t, r, t], ['L', r, t, l, b], ['L', l, b, r, b]]], width: (Z_WIDTH + STROKE) * s };
  },
  c: (x, cy, s) => {
    const open = 0.733; // half the mouth, radians
    const cx = x + (STROKE / 2 + BOWL) * s;
    // The mouth stops short of the full bowl, so the next letter tucks in.
    return { strokes: [[['A', cx, cy, BOWL * s, -open, open - TAU]]], width: (2 * BOWL + STROKE - 7) * s };
  },
  o: (x, cy, s) => {
    const cx = x + (STROKE / 2 + BOWL) * s;
    return { strokes: [[['A', cx, cy, BOWL * s, Math.PI, TAU], ['A', cx, cy, BOWL * s, TAU, 3 * Math.PI]]], width: (2 * BOWL + STROKE) * s };
  },
  m: (x, cy, s) => {
    const l = x + (STROKE / 2) * s;
    const t = cy - (BODY / 2) * s;
    const b = cy + (BODY / 2) * s;
    const a = ARCH * s;
    const arch = (from) => [['A', from + a, t + a, a, Math.PI, TAU], ['L', from + 2 * a, t + a, from + 2 * a, b]];
    return { strokes: [[['L', l, b, l, t]], arch(l), arch(l + 2 * a)], width: (4 * ARCH + STROKE) * s };
  },
};

/** The word "zcom" starting at `x`, centred on `cy`, at scale `s`. */
export const wordmark = (x, cy, s = 1) => {
  let cursor = x;
  const letters = [...'zcom'].map((ch, k) => {
    const { strokes, width } = LETTERS[ch](cursor, cy, s);
    cursor += width + GAP * s;
    return { ch, k, strokes, d: strokes.map(strokePath).join('') };
  });
  return { letters, weight: round(STROKE * s), right: cursor - GAP * s };
};

/** Distance from a point to the centre line of the nearest letter. */
export const distanceToWord = (word, x, y) => {
  let best = Infinity;
  for (const letter of word.letters) {
    for (const stroke of letter.strokes) {
      for (const piece of stroke) best = Math.min(best, distanceToPiece(piece, x, y));
    }
  }
  return best;
};

// ── Spiral ─────────────────────────────────────────────────────────────────

const SPREAD = 0.75;    // >0.5 opens the spiral outwards, so outer dots can grow
const FILL = 0.4;       // dot radius as a share of the room around it
const HUE_AT_3_OCLOCK = 0.46; // puts the brand blues on the left, next to the word

/**
 * The dots of a spiral of radius `radius` centred on (cx, cy). `skip` leaves
 * the eye of the spiral empty; `keep(dot)` drops the dots it refuses.
 * Each dot: x, y, r, fill, order (0… in drawing order), reach (0–1 from the
 * centre) and turn (0–1 round the wheel) — the last three time the animation.
 */
export const spiral = ({ cx, cy, radius, count, skip = 8, keep = () => true }) => {
  const total = count + skip;
  const dots = [];
  for (let n = skip; n < total; n += 1) {
    const reach = (n / total) ** SPREAD;
    const dist = radius * reach;
    const angle = n * GOLDEN_ANGLE;
    const room = Math.sqrt((TAU * SPREAD * radius ** (1 / SPREAD) * dist ** (2 - 1 / SPREAD)) / total);
    const dot = { x: cx + dist * Math.cos(angle), y: cy + dist * Math.sin(angle), r: room * FILL };
    if (!keep(dot)) continue;
    const turn = ((((angle / TAU) % 1) + 1) % 1 + HUE_AT_3_OCLOCK) % 1;
    dots.push({
      x: round(dot.x), y: round(dot.y), r: round(dot.r),
      fill: colourAt(turn),
      order: dots.length,
      reach: round(reach),
      turn: Math.round(turn * 1000) / 1000,
    });
  }
  return dots;
};

// ── Lockups ────────────────────────────────────────────────────────────────

const HALO = 6; // clear space kept between the word and the nearest dot

const badge = () => {
  const width = 290;
  const height = 200;
  const word = wordmark(6, 100, 0.85);
  const clear = word.weight / 2 + HALO;
  const dots = spiral({
    cx: 190, cy: 100, radius: 90, count: 170, skip: 10,
    keep: (dot) => distanceToWord(word, dot.x, dot.y) > clear + dot.r,
  });
  return { width, height, word, dots };
};

const inline = () => {
  const height = 128;
  const dots = spiral({ cx: 64, cy: 64, radius: 58, count: 84, skip: 5 });
  const word = wordmark(140, 64, 1);
  return { width: Math.ceil(word.right) + 4, height, word, dots };
};

const LOCKUPS = { badge, inline };
const built = {};

/** Geometry of a lockup ('badge' | 'inline'), built once. */
export const zcomLogo = (lockup = 'inline') => {
  const name = LOCKUPS[lockup] ? lockup : 'inline';
  if (!built[name]) built[name] = LOCKUPS[name]();
  return built[name];
};
